<template>
  <div class="tw-relative">
    <!-- Config dropdown using dropdown-menu components -->
    <DropdownMenu v-model:open="showConfigDropdown">
      <DropdownMenuTrigger
        data-config-manager
        class="tw-flex tw-min-w-64 tw-text-xs tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-secondary-500 tw-bg-white tw-border tw-border-solid tw-border-secondary-500 tw-rounded-md hover:tw-bg-secondary-500 hover:tw-text-white tw-outline-none"
      >
        <div class="tw-flex tw-items-center tw-w-full">
          <div>
            <BookmarkIcon />
          </div>
          <span class="tw-ml-2">
            {{ currentConfig?.name || t('common.forms.table_views') }}
          </span>
          <Badge
            v-if="canSaveState"
            variant="custom"
            class="tw-ml-2 tw-text-xs tw-px-1.5 tw-py-0.5 tw-bg-red-200 tw-text-red-600"
          >
            {{ t('common.forms.unsaved') }}
          </Badge>
          <span class="tw-ml-auto tw-flex tw-items-center">
            <ChevronDownIcon
              :class="{ 'tw-rotate-180': showConfigDropdown }"
              class="tw-transition-transform tw-duration-200 tw-w-4 tw-h-4"
            />
          </span>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="start"
        class="tw-min-w-64 tw-max-h-64 tw-overflow-y-auto !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
      >
        <!-- Saved configs list -->
        <div
          v-if="savedConfigs.length === 0"
          class="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500 tw-text-center"
        >
          {{ t('common.forms.no_saved_views') }}
        </div>

        <DropdownMenuItem
          v-for="config in savedConfigs"
          :key="config.id"
          @click="applyConfig(config.id!)"
          class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-cursor-pointer"
          :class="{
            'tw-bg-primary-50 tw-text-primary-700': config.id === currentConfig?.id,
          }"
        >
          <span>{{ config.name }}</span>

          <div class="tw-flex tw-items-center tw-gap-1 tw-ml-2">
            <Tooltip v-if="!config.isDefault">
              <TooltipTrigger as-child>
                <Button
                  @click.stop="setAsDefault(config.id!)"
                  variant="ghost"
                  size="icon"
                  class="tw-text-gray-400 hover:tw-text-primary-600"
                >
                  <StarIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent class="tw-bg-secondary-500">
                {{ t('common.forms.set_as_default') }}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger as-child>
                <Button
                  @click.stop="
                    showDeleteModal = true;
                    configToDelete = config;
                    showConfigDropdown = false;
                  "
                  variant="ghost"
                  size="icon"
                  class="tw-text-gray-400 hover:tw-text-red-600"
                >
                  <TrashIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent class="tw-bg-secondary-500">
                {{ t('common.forms.delete') }}
              </TooltipContent>
            </Tooltip>
          </div>
        </DropdownMenuItem>

        <!-- Actions -->
        <DropdownMenuSeparator />
        <DropdownMenuItem
          v-if="canSaveState && currentConfig?.id"
          @click="handleUpdateCurrentView"
          class="tw-text-primary-600 hover:tw-bg-primary-50 tw-cursor-pointer"
        >
          {{ t('common.forms.update_current_view') }}
        </DropdownMenuItem>
        <DropdownMenuItem
          v-if="canSaveState"
          @click="
            showSaveModal = true;
            showConfigDropdown = false;
          "
          class="tw-text-primary-600 hover:tw-bg-primary-50 tw-cursor-pointer"
        >
          {{ t('common.forms.save_as_new_view') }}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Save config modal -->
    <GenericModal
      v-model="showSaveModal"
      :title="t('common.forms.save_table_view')"
      close-on-backdrop
    >
      <form @submit.prevent="saveConfig">
        <div class="tw-mb-4">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            {{ t('common.forms.view_name') }}
          </label>
          <input
            v-model="newConfigName"
            type="text"
            required
            class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
            :placeholder="t('common.forms.enter_view_name')"
          />
        </div>

        <div class="tw-mb-6 tw-text-sm">
          <FancyCheckbox
            v-model="setAsDefaultOnSave"
            :label="t('common.forms.set_as_default_view')"
          />
        </div>
      </form>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-3">
          <button
            type="button"
            @click="showSaveModal = false"
            class="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
          >
            {{ t('common.forms.cancel') }}
          </button>
          <button
            @click="saveConfig"
            :disabled="!newConfigName.trim() || isSaving"
            class="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-primary-600 tw-rounded-md hover:tw-bg-primary-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {{ isSaving ? t('common.forms.saving') : t('common.forms.save') }}
          </button>
        </div>
      </template>
    </GenericModal>

    <!-- Rename config modal -->
    <GenericModal
      v-model="showRenameModal"
      :title="t('common.forms.rename_view')"
      close-on-backdrop
    >
      <form @submit.prevent="renameConfig">
        <div class="tw-mb-6">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            {{ t('common.forms.view_name') }}
          </label>
          <input
            v-model="renameConfigName"
            type="text"
            required
            class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
            :placeholder="t('common.forms.enter_view_name')"
          />
        </div>
      </form>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-3">
          <button
            type="button"
            @click="showRenameModal = false"
            class="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
          >
            {{ t('common.forms.cancel') }}
          </button>
          <button
            @click="renameConfig"
            :disabled="!renameConfigName.trim() || isSaving"
            class="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-primary-600 tw-rounded-md hover:tw-bg-primary-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {{ isSaving ? t('common.forms.saving') : t('common.forms.rename') }}
          </button>
        </div>
      </template>
    </GenericModal>

    <!-- Delete confirmation modal -->
    <GenericModal
      v-model="showDeleteModal"
      :title="t('common.forms.confirm_delete')"
      close-on-backdrop
    >
      <p class="tw-text-sm tw-text-gray-600">
        {{
          t('common.forms.delete_view_confirmation', {
            name: configToDelete?.name,
          })
        }}
      </p>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-3">
          <button
            @click="showDeleteModal = false"
            class="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
          >
            {{ t('common.forms.cancel') }}
          </button>
          <button
            @click="deleteConfig"
            :disabled="isDeleting"
            class="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-red-600 tw-rounded-md hover:tw-bg-red-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {{ isDeleting ? t('common.forms.deleting') : t('common.forms.delete') }}
          </button>
        </div>
      </template>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronDownIcon } from 'lucide-vue-next';

import StarIcon from '@/assets/icons/Star.svg';
import TrashIcon from '@/assets/icons/Trash.svg';
import BookmarkIcon from '@/assets/icons/Bookmark.svg';
import GenericModal from '@/components/shared/GenericModal.vue';
import FancyCheckbox from '@/components/shared/fancyTables/FancyCheckbox.vue';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const { t } = useI18n();

const props = defineProps<{
  savedConfigs: any[];
  currentConfig: any | null;
  isLoading: boolean;
  canSaveState: boolean;
}>();

const emit = defineEmits<{
  'save-config': [configName: string, setAsDefault: boolean];
  'apply-config': [configId: number];
  'delete-config': [configId: number];
  'rename-config': [configId: number, newName: string];
  'mark-as-default': [configId: number];
  'update-config': [configId: number];
}>();

// State
const showConfigDropdown = ref(false);
const showSaveModal = ref(false);
const showRenameModal = ref(false);
const showDeleteModal = ref(false);
const newConfigName = ref('');
const renameConfigName = ref('');
const setAsDefaultOnSave = ref(false);
const configToDelete = ref<any | null>(null);
const isSaving = ref(false);
const isDeleting = ref(false);

// Methods
const saveConfig = async () => {
  if (!newConfigName.value.trim() || !props.canSaveState) return;

  isSaving.value = true;
  try {
    emit('save-config', newConfigName.value.trim(), setAsDefaultOnSave.value);
    showSaveModal.value = false;
    newConfigName.value = '';
    setAsDefaultOnSave.value = false;
  } finally {
    isSaving.value = false;
  }
};

const renameConfig = async () => {
  if (!renameConfigName.value.trim() || !props.currentConfig?.id) return;

  isSaving.value = true;
  try {
    emit('rename-config', props.currentConfig.id, renameConfigName.value.trim());
    showRenameModal.value = false;
    renameConfigName.value = '';
  } finally {
    isSaving.value = false;
  }
};

const applyConfig = (configId: number) => {
  emit('apply-config', configId);
  showConfigDropdown.value = false;
};

const handleUpdateCurrentView = () => {
  emit('update-config', props.currentConfig?.id);
  showConfigDropdown.value = false;
};

const deleteConfig = async () => {
  if (!configToDelete.value?.id) return;

  isDeleting.value = true;
  try {
    emit('delete-config', configToDelete.value.id);
    showDeleteModal.value = false;
    configToDelete.value = null;
  } finally {
    isDeleting.value = false;
  }
};

const setAsDefault = (configId: number) => {
  emit('mark-as-default', configId);
  showConfigDropdown.value = false;
};
</script>
